/**
 * AnalyticsSystem - Tracks learning analytics events
 * From PRD Section 10.2
 */

import Phaser from 'phaser';
import type { AnalyticsEvent, AnalyticsEventType } from '@/types';

const STORAGE_KEY = 'cellsandlife_analytics';
const MAX_EVENTS = 1000;

export interface AnalyticsConfig {
  /** Enable analytics tracking */
  enabled?: boolean;
  /** Session ID (auto-generated if not provided) */
  sessionId?: string;
  /** User/student ID (optional) */
  userId?: string;
  /** Flush events to storage after this many events */
  flushThreshold?: number;
}

export interface SessionAnalytics {
  sessionId: string;
  startTime: Date;
  endTime?: Date;
  duration: number;
  eventsCount: number;
  levelStarts: number;
  levelCompletes: number;
  classificationsAttempted: number;
  classificationsCorrect: number;
  hintsRequested: number;
  conceptsIntroduced: string[];
}

/**
 * AnalyticsSystem tracks gameplay events for learning analytics
 */
export class AnalyticsSystem {
  private scene: Phaser.Scene;
  private config: Required<AnalyticsConfig>;
  private events: AnalyticsEvent[] = [];
  private sessionStartTime: number;
  private pendingEvents: number = 0;

  // Session counters
  private levelStarts = 0;
  private levelCompletes = 0;
  private classificationsAttempted = 0;
  private classificationsCorrect = 0;
  private hintsRequested = 0;
  private conceptsIntroduced: Set<string> = new Set();

  constructor(scene: Phaser.Scene, config: AnalyticsConfig = {}) {
    this.scene = scene;
    this.sessionStartTime = Date.now();

    this.config = {
      enabled: config.enabled ?? true,
      sessionId: config.sessionId ?? `session_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      userId: config.userId ?? undefined,
      flushThreshold: config.flushThreshold ?? 50,
    } as Required<AnalyticsConfig>;

    this.loadEvents();
    this.setupEventListeners();

    // Track session start
    this.trackEvent('level_start', { type: 'session_start' });
  }

  /**
   * Set up automatic event listeners
   */
  private setupEventListeners(): void {
    // Level events
    this.scene.events.on('level:start', (data: Record<string, unknown>) => {
      this.levelStarts++;
      this.trackEvent('level_start', data);
    });

    this.scene.events.on('level:complete', (data: Record<string, unknown>) => {
      this.levelCompletes++;
      this.trackEvent('level_complete', data);
    });

    // Classification events
    this.scene.events.on(
      'classification:complete',
      (data: { result: { correct: boolean } }) => {
        this.classificationsAttempted++;
        if (data.result.correct) {
          this.classificationsCorrect++;
        }
        this.trackEvent('classification_attempt', {
          correct: data.result.correct,
        });
      }
    );

    // Evidence events
    this.scene.events.on('evidence:collected', (data: Record<string, unknown>) => {
      this.trackEvent('evidence_collected', data);
    });

    // Hint events
    this.scene.events.on('hint:requested', (data: Record<string, unknown>) => {
      this.hintsRequested++;
      this.trackEvent('hint_requested', data);
    });

    // Concept events
    this.scene.events.on('concept:introduced', (data: { conceptId: string }) => {
      this.conceptsIntroduced.add(data.conceptId);
      this.trackEvent('concept_introduced', data);
    });

    this.scene.events.on('concept:assessed', (data: Record<string, unknown>) => {
      this.trackEvent('concept_assessed', data);
    });

    // Difficulty events
    this.scene.events.on('difficulty:adjusted', (data: Record<string, unknown>) => {
      this.trackEvent('difficulty_adjusted', data);
    });

    // Tutorial events
    this.scene.events.on('tutorial:skipped', (data: Record<string, unknown>) => {
      this.trackEvent('tutorial_skipped', data);
    });
  }

  /**
   * Track a custom analytics event
   */
  public trackEvent(eventType: AnalyticsEventType, data: Record<string, unknown> = {}): void {
    if (!this.config.enabled) return;

    const event: AnalyticsEvent = {
      eventType,
      timestamp: new Date(),
      sessionId: this.config.sessionId,
      userId: this.config.userId,
      data: {
        ...data,
        levelId: this.scene.registry.get('currentLevelId'),
        chapterId: this.scene.registry.get('currentChapterId'),
      },
    };

    this.events.push(event);
    this.pendingEvents++;

    // Emit for real-time listeners
    this.scene.events.emit('analytics:event', event);

    // Flush to storage periodically
    if (this.pendingEvents >= this.config.flushThreshold) {
      this.flush();
    }
  }

  /**
   * Get session analytics summary
   */
  public getSessionAnalytics(): SessionAnalytics {
    const duration = (Date.now() - this.sessionStartTime) / 1000;

    return {
      sessionId: this.config.sessionId,
      startTime: new Date(this.sessionStartTime),
      duration,
      eventsCount: this.events.filter((e) => e.sessionId === this.config.sessionId).length,
      levelStarts: this.levelStarts,
      levelCompletes: this.levelCompletes,
      classificationsAttempted: this.classificationsAttempted,
      classificationsCorrect: this.classificationsCorrect,
      hintsRequested: this.hintsRequested,
      conceptsIntroduced: Array.from(this.conceptsIntroduced),
    };
  }

  /**
   * Get all events (optionally filtered)
   */
  public getEvents(filter?: {
    eventType?: AnalyticsEventType;
    sessionId?: string;
    since?: Date;
  }): AnalyticsEvent[] {
    let filtered = this.events;

    if (filter?.eventType) {
      filtered = filtered.filter((e) => e.eventType === filter.eventType);
    }

    if (filter?.sessionId) {
      filtered = filtered.filter((e) => e.sessionId === filter.sessionId);
    }

    if (filter?.since) {
      filtered = filtered.filter((e) => e.timestamp >= filter.since!);
    }

    return filtered;
  }

  /**
   * Get events for current session
   */
  public getCurrentSessionEvents(): AnalyticsEvent[] {
    return this.getEvents({ sessionId: this.config.sessionId });
  }

  /**
   * Export analytics as JSON
   */
  public exportToJSON(): string {
    const data = {
      exportDate: new Date().toISOString(),
      sessionAnalytics: this.getSessionAnalytics(),
      events: this.events.map((e) => ({
        ...e,
        timestamp: e.timestamp.toISOString(),
      })),
    };

    return JSON.stringify(data, null, 2);
  }

  /**
   * Export analytics as CSV
   */
  public exportToCSV(): string {
    const headers = ['Timestamp', 'Event Type', 'Session ID', 'User ID', 'Data'];

    const rows = this.events.map((e) => [
      e.timestamp.toISOString(),
      e.eventType,
      e.sessionId,
      e.userId || '',
      `"${JSON.stringify(e.data).replace(/"/g, '""')}"`,
    ]);

    return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  }

  /**
   * Download analytics export
   */
  public downloadExport(format: 'json' | 'csv'): void {
    const data = format === 'json' ? this.exportToJSON() : this.exportToCSV();
    const mimeType = format === 'json' ? 'application/json' : 'text/csv';
    const filename = `cells-and-life-analytics-${this.config.sessionId}.${format}`;

    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
  }

  /**
   * Flush events to localStorage
   */
  public flush(): void {
    this.saveEvents();
    this.pendingEvents = 0;
  }

  /**
   * Load events from localStorage
   */
  private loadEvents(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.events = (parsed.events || []).map(
          (e: AnalyticsEvent & { timestamp: string }) => ({
            ...e,
            timestamp: new Date(e.timestamp),
          })
        );

        // Trim old events
        if (this.events.length > MAX_EVENTS) {
          this.events = this.events.slice(-MAX_EVENTS);
        }
      }
    } catch (e) {
      console.warn('Failed to load analytics:', e);
      this.events = [];
    }
  }

  /**
   * Save events to localStorage
   */
  private saveEvents(): void {
    try {
      // Trim old events before saving
      if (this.events.length > MAX_EVENTS) {
        this.events = this.events.slice(-MAX_EVENTS);
      }

      const serializable = {
        events: this.events.map((e) => ({
          ...e,
          timestamp: e.timestamp.toISOString(),
        })),
        lastUpdated: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
    } catch (e) {
      console.warn('Failed to save analytics:', e);
    }
  }

  /**
   * Set user ID (for when user logs in or identifies themselves)
   */
  public setUserId(userId: string): void {
    this.config.userId = userId;
  }

  /**
   * Enable/disable analytics
   */
  public setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Check if analytics is enabled
   */
  public isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Clear all analytics data
   */
  public clearAll(): void {
    this.events = [];
    this.levelStarts = 0;
    this.levelCompletes = 0;
    this.classificationsAttempted = 0;
    this.classificationsCorrect = 0;
    this.hintsRequested = 0;
    this.conceptsIntroduced.clear();

    localStorage.removeItem(STORAGE_KEY);

    this.scene.events.emit('analytics:cleared');
  }

  /**
   * End session and record duration
   */
  public endSession(): void {
    const duration = (Date.now() - this.sessionStartTime) / 1000;

    this.trackEvent('session_duration', {
      duration,
      sessionAnalytics: this.getSessionAnalytics(),
    });

    this.flush();
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.endSession();

    // Remove all listeners
    this.scene.events.off('level:start');
    this.scene.events.off('level:complete');
    this.scene.events.off('classification:complete');
    this.scene.events.off('evidence:collected');
    this.scene.events.off('hint:requested');
    this.scene.events.off('concept:introduced');
    this.scene.events.off('concept:assessed');
    this.scene.events.off('difficulty:adjusted');
    this.scene.events.off('tutorial:skipped');
  }
}
